import { Colors } from "./config";
import { getLoader } from "./Customisation";

/**
 * Loading manager displays a loading spinner when a load is in progress
 */
export default class LoadingManager {
  constructor(container, stylesheet, isThumbnail) {
    this._count = 0;
    this.addLoadingContainer(container, stylesheet, isThumbnail);
  }

  /**
   * Adds the loading container to the provided element
   *
   * @param {Element} container
   * @param {jss.StyleSheet} stylesheet
   * @param {boolean} isThumbnail
   */
  addLoadingContainer(container, stylesheet, isThumbnail) {
    const loading = document.createElement("div");
    stylesheet.addRules(styles);

    if (isThumbnail) {
      loading.className = stylesheet.classes.loadingContainerBurgess;

      const burgessAnimation = document.createElement("img");
      burgessAnimation.src = getLoader().src;
      burgessAnimation.className = stylesheet.classes.loadingImageBurgess;

      loading.appendChild(burgessAnimation);
    } else {
      loading.className = stylesheet.classes.loadingContainer;
      const spinner = document.createElement("div");
      spinner.className = stylesheet.classes.spinner;

      const dot1 = document.createElement("div");
      dot1.className = stylesheet.classes.dot1;

      const dot2 = document.createElement("div");
      dot2.className = stylesheet.classes.dot2;

      spinner.appendChild(dot1);
      spinner.appendChild(dot2);
      loading.appendChild(spinner);
    }

    this.stylesheet = stylesheet;
    this.container = loading;
    container.appendChild(loading);
    if (isThumbnail) {
      this.show();
    }
  }

  /**
   * Trigger start of a load.
   *
   * A function is returned which should be called when load is completed.
   *
   * @return {function} callback - finished loading callback
   */
  start(noDelay = false) {
    this._count += 1;

    const delay = setTimeout(
      () => {
        if (hasFinished === false && this._count > 0) {
          this.show();
        }
      },
      noDelay ? 0 : 1000,
    );

    let hasFinished = false;

    return () => {
      if (hasFinished === false) {
        clearTimeout(delay);
        hasFinished = true;
        this._count -= 1;

        if (this._count <= 0) {
          this.hide();
        }
      }
    };
  }

  /**
   * Display the loading container
   */
  show() {
    this.container.classList.add("show");
  }

  /**
   * Hide the loading container
   */
  hide() {
    this.container.classList.remove("show");
  }
}

const styles = {
  // Loading manager
  loadingContainer: {
    width: "100%",
    height: "100%",
    display: "none",
    position: "absolute",
    zIndex: 1000,
    top: 0,
    left: 0,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    "&.show": {
      display: "block",
    },
    "& .four": {
      width: "100px",
      height: "100px",
      borderRadius: "50%",
      opacity: 0.8,
      "border-top": `${Colors.White} 3px solid`,
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%) rotate(0)",
      position: "absolute",
      animationName: "rotateRight",
      animationDuration: "1s",
      "animation-iteration-count": "infinite",
      "transition-property": "transform",
    },
    "& .three": {
      width: "90px",
      height: "90px",
      borderRadius: "50%",
      opacity: 0.8,
      "border-right": `${Colors.White} 3px solid`,
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%) rotate(0)",
      position: "absolute",
      animationName: "rotateLeft",
      animationDuration: "1s",
      "animation-iteration-count": "infinite",
      "transition-property": "transform",
    },
    "& .two": {
      width: "80px",
      height: "80px",
      borderRadius: "50%",
      opacity: 0.8,
      "border-bottom": `${Colors.White} 3px solid`,
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%) rotate(0)",
      position: "absolute",
      animationName: "rotateRight",
      animationDuration: "1s",
      "animation-iteration-count": "infinite",
      "transition-property": "transform",
    },
    "& .one": {
      width: "70px",
      height: "70px",
      borderRadius: "50%",
      opacity: 0.8,
      "border-left": `${Colors.White} 3px solid`,
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%) rotate(0)",
      position: "absolute",
      animationName: "rotateLeft",
      animationDuration: "1s",
      "animation-iteration-count": "infinite",
      "transition-property": "transform",
    },
  },
  loadingContainerBurgess: {
    width: "100%",
    height: "100%",
    position: "absolute",
    zIndex: 1000,
    top: 0,
    left: 0,
    backgroundColor: "rgba(23, 190, 218, 0.7)",
    transition: "visibility 0.75s, opacity 0.75s ease-in",
    opacity: 0,
    visibility: "hidden",

    "&.show": {
      opacity: 1,
      visibility: "initial",
      transition: "visibility 0s, opacity 0s linear",
    },
  },
  loadingImageBurgess: {
    maxWidth: "80%",
    maxHeight: "80%",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    opacity: 0.8,
    position: "absolute",
  },
  "@keyframes rotateLeft": {
    from: { transform: "translate(-50%, -50%) rotate(0deg)" },
    to: { transform: "translate(-50%, -50%) rotate(-360deg)" },
  },
  "@keyframes rotateRight": {
    from: { transform: "translate(-50%, -50%) rotate(0deg)" },
    to: { transform: "translate(-50%, -50%) rotate(360deg)" },
  },
  spinner: {
    display: "inline-block",
    top: "50%",
    width: "35px",
    height: "35px",
    position: "relative",
    textAlign: "center",
    "-webkit-animation": "sk-rotate 2.0s infinite linear",
    animation: "sk-rotate 2.0s infinite linear",
  },
  dot1: {
    width: "60%",
    height: "60%",
    display: "inline-block",
    position: "absolute",
    top: 0,
    backgroundColor: "white",
    borderRadius: "100%",
    "-webkit-animation": "sk-bounce 2.0s infinite ease-in-out",
    animation: "sk-bounce 2.0s infinite ease-in-out",
  },
  dot2: {
    width: "60%",
    height: "60%",
    display: "inline-block",
    position: "absolute",
    backgroundColor: "white",
    borderRadius: "100%",
    "-webkit-animation": "sk-bounce 2.0s infinite ease-in-out",
    animation: "sk-bounce 2.0s infinite ease-in-out",
    top: "auto",
    bottom: 0,
    "-webkit-animation-delay": "-1.0s",
    "animation-delay": "-1.0s",
  },
  "@keyframes sk-bounce": {
    "0%, 100%": {
      transform: "scale(0.0)",
      "-webkit-transform": "scale(0.0)",
    },
    "50%": {
      transform: "scale(1.0)",
      "-webkit-transform": "scale(1.0)",
    },
  },
};
